import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Attachment, RequisitionItem, Review, useGetItemTransfersQuery, useGetPurchaseOrdersQuery, useGetStakeholdersByTypeByTypeQuery } from '../../../redux/vmsApi';
import ComparativeAnalysisTable from './comparativeAnalysis';
import { formatCurrency } from "../../../utils/currencyUtil";
import { getStakeholderStatusBadges } from "../../../utils/stakeholderStatusUtil";

import PaymentRequest from './paymentRequest';

import { keyframes } from '@mui/system';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import RenderApproverName from "./RenderApproverName";
import RequisitionBomBudgetPanel from './RequisitionBomBudgetPanel';
import ContractItemsPreviewPanel from './ContractItemsPreviewPanel';


const pulse = keyframes`
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.04);
    }
    100% {
        transform: scale(1);
    }
`;


interface RenderRequisitionDetailsProps {
  procurementProcess: any;
  development: any;
  selectedSupplier: number | null;
  openDialog: string | null;
  handleDialogOpen: (url: string) => void;
  handleDialogClose: () => void;
  handleSelectSupplier: (index: number, quotation: any) => void;
  showQuotationForm: boolean;
  setShowQuotationForm: (show: boolean) => void;
  isInProcess: boolean;
}

const ReviewerTableRow: React.FC<{ review: Review }> = ({ review }) => {
  if (!review?.comment) {
    return null;
  }

  return (
    <TableRow>
      <TableCell>
        {new Date(review?.dateReviewed).toLocaleDateString('en-GB', {
          weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
        })}{' '}
        {new Date(review?.dateReviewed).toLocaleTimeString('en-GB', {
          hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
        })}
      </TableCell>
      <TableCell>
          {review?.user ? <RenderApproverName user={review.user!} /> : 'N/A'}
      </TableCell>

      <TableCell>
        {review?.comment}
      </TableCell>

      <TableCell>
        {review?.approved ? (
          <Chip label="Approved" color="success" />
        ) : (
          <Chip label="Rejected" color="error" />
        )}
      </TableCell>
    </TableRow>
  )
}

const RenderRequisitionDetails: React.FC<RenderRequisitionDetailsProps> = ({
  procurementProcess,
  development,
  selectedSupplier,
  openDialog,
  handleDialogOpen,
  handleDialogClose,
  handleSelectSupplier,
  isInProcess
}) => {
  const theme = useTheme();
  const isTabletOrSmaller = useMediaQuery(theme.breakpoints.down('md'));

  const hasComments = Boolean(
    procurementProcess?.siteManagerReview?.comment ||
    procurementProcess?.projectManagerReview?.comment ||
    procurementProcess?.cpoReview?.comment ||
    procurementProcess?.pcReview?.comment ||
    procurementProcess?.pmReview?.comment ||
    procurementProcess?.proChairReview?.comment
  );
  const getAlertMessage = () => {
    switch (procurementProcess?.currentTask) {
      case "SiteManagerReviewPurchaseRequisition":
        return "As Site Manager, review and either approve the requisition or request amendments in the box below.";
      case "CorrectPurchaseRequisition":
        return "As Site Agent, adjust the requisition according to reviewer feedback.";
      case "ProjectManagerReviewPurchaseRequisition":
        return "Review and either approve the requisition or request amendments in the box below.";
      // case "CPOReviewPurchaseRequisition":
      //   return "As Chief Procurement Officer, review and either approve the requisition or request amendments in the box below.";
      case "PCToUploadQuotations":
        return "As Procurement Clerk, upload supplier quotations for review or reject and request amendments in the box below.";
      case "PMToReviewProcurementApplication":
        return "Review supplier information and quotations by clicking on the supplier’s name.";
      case "CPOPicksSupplier":
        return "Select a supplier by clicking on its name.";
      case "ProChairReviewsSupplierChoice":
        return "As ProChair, review the selected supplier—click the name to view details.";
      case "PMToIssuePurchaseOrder":
        return "As Procurement Manager, issue the purchase order.";
      case "CFOAllocateFunds":
        return "Prepare  payment schedule.";
      case "COOToApprove":
        return "As COO, approve or reject payment requisitions for the approved purchase order.";
      case "CEOToApprove":
        return "As CEO, approve or reject payment requisitions for the approved purchase order.";

      default:
        return "No action required.";
    }
  };

  // Initializing Grvs
  const { data: itemTransfers } = useGetItemTransfersQuery({ procurementProcessId: procurementProcess.id });
  const [grvTotal, setGRVTotal] = useState(0);

  const { data: purchaseOrders, isLoading: poLoading, isError: poError } =
    useGetPurchaseOrdersQuery({ procurementProcessId: procurementProcess.id });

  const { data: stakeholders } = useGetStakeholdersByTypeByTypeQuery({ type: 'Supplier' });

  useEffect(() => {
    if (itemTransfers) {
      const total = itemTransfers.reduce((sum, transfer) => sum + (transfer.grandTotal || 0), 0);
      setGRVTotal(total);
    }
  }, [itemTransfers]);

  const getLocationName = (location: any) => {
    if (location?.unit?.name) return `Unit: ${location.unit.name}`;
    if (location?.project?.name) return `Project: ${location.project.name}`;
    if (location?.development?.name) return `Development: ${location.development.name}`;
    if (location?.inventoryLocation?.locationCode) return `Inventory: ${location.inventoryLocation.locationCode}`;
    if (location?.supplier?.name) return `Supplier: ${location.supplier.name}`;
    return 'N/A';
  };

  const items = procurementProcess?.requisition?.items || [];

  const showQuantity = items.some((item: RequisitionItem) => item.quantity?.quantityValue != null && item.quantity.quantityValue !== 0);
  const showStand = items.some((item: RequisitionItem) => !!item.unit?.name);
  const showProcured = items.some((item: RequisitionItem) => item.procured != null);
  const showUnitPrice = items.some((item: RequisitionItem) => item.rate != null && item.rate !== 0);
  const showTotalPrice = items.some((item: RequisitionItem) => item.totalCostPerItem != null && item.totalCostPerItem !== 0);
  const showPaymentRequest = procurementProcess?.requestType === 'FUNDS REQUEST'
    ? procurementProcess?.currentTask !== 'ProjectManagerReviewPurchaseRequisition'
    : !!procurementProcess?.purchaseOrderDocument?.url?.trim();

  return (
    <Card
      sx={{
        margin: { xs: 1, sm: 2 },
        padding: { xs: 1, sm: 2 },
        backgroundColor: '#f5f5f5',
        borderRadius: 4,
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        border: '1px solid #ccc',
      }}
    >
      <CardContent>
        <Typography
          variant="h4"
          gutterBottom
          color="primary"
          sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2rem' } }}
        >
          Requisition Details
        </Typography>
        <Grid container spacing={2} sx={{ marginBottom: 2 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body1" color="textSecondary">
              <strong>Number:</strong> {procurementProcess?.requisition.reqNumber}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1" color="textSecondary">
              <strong>Title:</strong> {procurementProcess?.requisition.title}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1" color="textSecondary">
              <strong>Date:</strong> {new Date(procurementProcess?.initiatedDate).toLocaleDateString('en-GB', {
                weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
              })}{' '}
              {new Date(procurementProcess?.initiatedDate).toLocaleTimeString('en-GB', {
                hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
              })}
            </Typography>

          </Grid>
        </Grid>
        <br />
        <Divider sx={{ marginY: 2 }} />
        <Typography variant="h5" gutterBottom color="secondary" sx={{ fontWeight: 'bold' }}>
          {procurementProcess?.requestType === 'FUNDS REQUEST' ? 'ALLOCATION' : 'Project Details'}
        </Typography>
        <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: 2, boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
        <Table
          size={isTabletOrSmaller ? 'small' : 'medium'}
          sx={{
            backgroundColor: '#ffffff',
            borderRadius: 2,
            minWidth: isTabletOrSmaller ? 640 : 'auto',
          }}
        >
          <TableHead sx={{ backgroundColor: '#1976d2', color: 'white' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Description</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Start Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              const matchingProject = development?.projects?.find(
                (p: { id: any }) => p.id === procurementProcess?.requisition?.project.id
              );

              if (matchingProject) {
                const renderStatusChip = (status: string) => {
                  switch (status) {
                    case 'Completed':
                      return <Chip label="Completed" color="success" sx={{ fontWeight: 'bold' }} />;
                    case 'Not Started':
                      return <Chip label="Not Started" color="default"
                        sx={{ fontWeight: 'bold', backgroundColor: '#ffc107', color: 'black' }} />;
                    case 'In Progress':
                      return <Chip label="In Progress" color="warning" sx={{ fontWeight: 'bold' }} />;
                    default:
                      return <Chip label={status} sx={{ fontWeight: 'bold' }} />;
                  }
                };

                return (
                  <TableRow key={matchingProject.id}>
                    <TableCell>{matchingProject.name}</TableCell>
                    <TableCell>{matchingProject.description}</TableCell>
                    <TableCell>{renderStatusChip(matchingProject.status)}</TableCell>
                    <TableCell>{matchingProject.startDate}</TableCell>
                  </TableRow>
                );
              }

              return (
                <TableRow>
                  <TableCell colSpan={4}>No matching project found.</TableCell>
                </TableRow>
              );
            })()}
          </TableBody>
        </Table>
        </TableContainer>

        <Divider sx={{ marginY: 2 }} />
        <Typography variant="h5" gutterBottom color="secondary" sx={{ fontWeight: 'bold' }}>
          Requisition Item(s) Details
        </Typography>

        <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <Table
          size={isTabletOrSmaller ? 'small' : 'medium'}
          sx={{ backgroundColor: '#fff', borderRadius: 2, minWidth: isTabletOrSmaller ? 720 : 'auto' }}
        >
          <TableHead sx={{ backgroundColor: '#1976d2' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Description</TableCell>
              {showQuantity && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Quantity</TableCell>}
              {showStand && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Stand</TableCell>}
              {showProcured && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Procured</TableCell>}
              {showUnitPrice && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Unit Price</TableCell>}
              {showTotalPrice && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Total Price Per Item</TableCell>}
            </TableRow>
          </TableHead>

          <TableBody>
            {items.length > 0 ? items.map((item: RequisitionItem, index: number) => (
              <TableRow key={index}>
                <TableCell>{item.description}</TableCell>
                {showQuantity && (
                  <TableCell>
                    {item.quantity?.quantityValue} {item.quantity?.quantityUnit}
                  </TableCell>
                )}
                {showStand && <TableCell>{item.unit?.name}</TableCell>}
                {showProcured && (
                  <TableCell>
                    {item.procured
                      ? <Chip label="Yes" color="success" />
                      : <Chip label="No" color="error" />}
                  </TableCell>
                )}
                {showUnitPrice && <TableCell>{item.rate}</TableCell>}
                {showTotalPrice && <TableCell>{item.totalCostPerItem}</TableCell>}
              </TableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={
                  1
                  + (showQuantity ? 1 : 0)
                  + (showStand ? 1 : 0)
                  + (showProcured ? 1 : 0)
                  + (showUnitPrice ? 1 : 0)
                  + (showTotalPrice ? 1 : 0)
                }>
                  No items to display.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </TableContainer>

        


        <Box sx={{ marginTop: 4, textAlign: 'center' }}>
          <Typography
            variant="h4"
            sx={{
              color: '#ff5722',
              fontWeight: 'bold',
              backgroundColor: '#fbe9e7',
              padding: { xs: 2, sm: 3 },
              borderRadius: '10px',
              boxShadow: '0px 4px 12px rgba(255, 87, 34, 0.3)',
              display: 'inline-block',
              fontSize: { xs: '1.2rem', sm: '2rem' },
            }}
          >
            Total Item(s) Price: ${procurementProcess?.requisition?.totalCost.toFixed(2)}
          </Typography>
        </Box>

        <Divider sx={{ marginY: 2 }} />
        <Typography variant="h5" gutterBottom color="secondary" sx={{ fontWeight: 'bold' }}>
          Requisition Supporting Documents
        </Typography>

        <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: 2, boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
        <Table size={isTabletOrSmaller ? 'small' : 'medium'} sx={{ backgroundColor: '#ffffff', borderRadius: 2, minWidth: isTabletOrSmaller ? 520 : 'auto' }}>
          <TableHead sx={{ backgroundColor: '#1976d2', color: 'white' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Document Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {procurementProcess?.requisition?.documents.length > 0 ? (
              procurementProcess?.requisition?.documents.map((document: Attachment, index: number) => (
                <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#e8f0fe' } }}>
                  <TableCell>{document.name}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleDialogOpen(document.name)} sx={{ color: '#1976d2' }}>
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2}>No documents available</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </TableContainer>


        <Divider sx={{ marginY: 2 }} />


        {procurementProcess?.requisitionDocument?.url &&
          procurementProcess.requisitionDocument.url.trim() !== '' && (
            <Typography variant="h5" gutterBottom color="secondary" sx={{ fontWeight: 'bold' }}>
              Requisition Document
            </Typography>
          )}


        {procurementProcess?.requisitionDocument?.url &&
          procurementProcess.requisitionDocument.url.trim() !== '' && (
            <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: 2, boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
            <Table size={isTabletOrSmaller ? 'small' : 'medium'} sx={{ backgroundColor: '#ffffff', borderRadius: 2, minWidth: isTabletOrSmaller ? 520 : 'auto' }}>
              <TableHead sx={{ backgroundColor: '#1976d2', color: 'white' }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Document Name</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {procurementProcess?.requisitionDocument ? (
                  <TableRow sx={{ '&:hover': { backgroundColor: '#e8f0fe' } }}>
                    <TableCell>{procurementProcess?.requisitionDocument.name}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleDialogOpen(procurementProcess?.requisitionDocument.name)}
                        sx={{ color: '#1976d2' }}>
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                ) : (
                  <TableRow>
                    <TableCell colSpan={2}>No documents available</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </TableContainer>)}

        <Divider sx={{ marginY: 2 }} />
        <Typography variant="h5" gutterBottom color="secondary" sx={{ fontWeight: 'bold' }}>
          Requisition Reviewer's Comments
        </Typography>
        <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: 2, boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
        <Table size={isTabletOrSmaller ? 'small' : 'medium'} sx={{ backgroundColor: '#ffffff', borderRadius: 2, minWidth: isTabletOrSmaller ? 720 : 'auto' }}>
          <TableHead sx={{ backgroundColor: '#1976d2', color: 'white' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date Reviewed</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reviewer</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Comment</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Decision</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <ReviewerTableRow review={procurementProcess?.siteManagerReview} />
            <ReviewerTableRow review={procurementProcess?.projectManagerReview} />
            <ReviewerTableRow review={procurementProcess?.pcReview} />

            {!hasComments && (
              <TableRow>
                <TableCell colSpan={4}>No comments from reviewers.</TableCell>
              </TableRow>
            )}

          </TableBody>
        </Table>
        </TableContainer>
        <br />

        {isInProcess && (
          <Alert
            severity="warning"
            icon={<WarningAmberIcon fontSize="large" />}
            sx={{
              backgroundColor: '#fff3e0',
              color: '#3A2D26',
              borderRadius: 2,
              padding: '16px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >

            <Typography
              variant="subtitle1"
              component="div"
              sx={{
                fontWeight: 'bold',
                fontSize: '1.2rem',
                lineHeight: 1,
                ml: 1,
              }}
            >
              {getAlertMessage()}
            </Typography>
          </Alert>
        )}

        <RequisitionBomBudgetPanel
          projectId={procurementProcess?.requisition?.project?.id}
          items={items}
        />

        <ContractItemsPreviewPanel quotations={procurementProcess?.pcReview?.quotations} />

        {procurementProcess?.pcReview?.quotations && (
          <>
            <Divider sx={{ marginY: 2 }} />
            <ComparativeAnalysisTable quotations={procurementProcess.pcReview.quotations}
              user={procurementProcess.pcReview.user}
              dateReviewed={procurementProcess.pcReview.dateReviewed}
              projectId={procurementProcess?.requisition?.project?.id} />
            <Typography variant="h5" gutterBottom color="secondary" sx={{ fontWeight: 'bold' }}>
              {procurementProcess?.currentTask === "CPOPicksSupplier"
                ? "Select Supplier"
                : "Review Supplier Quotations"}
            </Typography>

            {procurementProcess.pcReview.quotations.map((quotation: any, index: number) => (
              <Accordion
                key={index}
                expanded={selectedSupplier === index || quotation.selected}
                sx={{
                  backgroundColor: selectedSupplier === index ? '#e3f2fd' : '#f0f4fa',
                  borderRadius: 1,
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  marginBottom: 2,
                }}
              >
                <Tooltip
                  title={
                    procurementProcess?.currentTask === "PMToReviewProcurementApplication" ||
                      procurementProcess?.currentTask === "ProChairReviewsSupplierChoice"
                      ? "Click to view supplier details & Quotations"
                      : procurementProcess?.currentTask === "CPOPicksSupplier"
                        ? "Click to pick a supplier"
                        : "Click to select supplier"
                  }
                >
                  <AccordionSummary
                    onClick={() => {
                      if (procurementProcess?.currentTask === "CPOPicksSupplier") {
                        handleSelectSupplier(index, quotation);
                      } else if (!quotation.selected) {
                        handleSelectSupplier(index, quotation);
                      }
                    }}
                    expandIcon={<ExpandMoreIcon sx={{ color: '#ffffff' }} />}
                    aria-controls={`panel${index}-content`}
                    id={`panel${index}-header`}
                    sx={{
                      backgroundColor: '#1976d2',
                      color: 'white',
                      borderRadius: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" useFlexGap>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
                        {quotation.supplier.name}
                      </Typography>
                      {getStakeholderStatusBadges(
                        stakeholders?.find((stakeholder: any) => stakeholder.id === quotation.supplier.id)
                      ).map((badge) => (
                        <Chip
                          key={badge.label}
                          label={badge.label}
                          color={badge.color as any}
                          variant={badge.variant}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      ))}
                    </Stack>

                    {procurementProcess?.currentTask === "CPOPicksSupplier" ? (
                      selectedSupplier === index && (
                        <Badge color="secondary" variant="dot">
                          <CheckCircleIcon sx={{ color: '#F7C914', marginLeft: 2, fontSize: '2rem' }} />
                        </Badge>
                      )
                    ) : (
                      quotation.selected && (
                        <Badge color="secondary" variant="dot">
                          <CheckCircleIcon sx={{ color: '#F7C914', marginLeft: 2, fontSize: '2rem' }} />
                        </Badge>
                      )
                    )}
                  </AccordionSummary>
                </Tooltip>
                <AccordionDetails>
                  <Box sx={{ marginBottom: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, p: 1.5, borderRadius: 2, borderLeft: '5px solid #1C4697', backgroundColor: '#eaf1fb' }}>
                      <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary' }}>
                        Payment Terms
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1C4697' }}>
                        {quotation.paymentUpfrontPercentage != null && quotation.paymentUpfrontPercentage !== ''
                          ? `${quotation.paymentUpfrontPercentage}% Payment Upfront`
                          : 'Not specified'}
                      </Typography>
                      {quotation.deliveryLeadTime ? (
                        <>
                          <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary', ml: 2 }}>
                            Lead Time
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1C4697' }}>
                            {quotation.deliveryLeadTime} day(s)
                          </Typography>
                        </>
                      ) : null}
                    </Box>
                    {(quotation as any).termsAndConditions && (
                      <Box sx={{ mb: 1.5, p: 1.5, borderRadius: 2, borderLeft: '5px solid #2e7d32', backgroundColor: '#edf7ee' }}>
                        <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary' }}>
                          Terms &amp; Conditions / SLA
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#1b5e20', mt: 0.5 }}>
                          {(quotation as any).termsAndConditions}
                        </Typography>
                      </Box>
                    )}
                    {quotation.remarks &&
                      <Typography
                        variant="body1"
                        sx={{
                          display: 'inline-block',
                          marginBottom: 1,
                          padding: 2,
                          backgroundColor: 'yellow',
                          borderLeft: '4px solid #2196f3',
                          borderRadius: 1,
                          animation: `${pulse} 2s infinite`,
                        }}
                      >
                        <strong>Remarks:</strong> {quotation.remarks}
                      </Typography>
                    }
                  </Box>

                  <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: 1, boxShadow: 'none' }}>
                  <Table size={isTabletOrSmaller ? 'small' : 'medium'} sx={{ width: '100%', backgroundColor: '#ffffff', borderRadius: 1, minWidth: isTabletOrSmaller ? 480 : 'auto' }}>
                    <TableBody>
                      {quotation?.attachments?.map((attachment: any, i: any) => (
                        <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#e8f0fe' } }}>
                          <TableCell>{attachment.name}</TableCell>
                          <TableCell>
                            <IconButton onClick={() => handleDialogOpen(attachment.name)} sx={{ color: '#1976d2' }}>
                              <VisibilityIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </TableContainer>

                  <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: 1, boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                  <Table
                    size={isTabletOrSmaller ? 'small' : 'medium'}
                    sx={{
                      width: '100%',
                      backgroundColor: '#f9f9f9',
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid #e0e0e0',
                      minWidth: isTabletOrSmaller ? 760 : 'auto',
                    }}
                  >
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#1976d2' }}>
                        <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Description</TableCell>
                        <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Discount</TableCell>
                        <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Quantity</TableCell>
                        <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Unit</TableCell>
                        <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Rate</TableCell>
                        <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Total Cost</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {quotation?.quotationItems?.map((item: any, i: number) => (
                        <TableRow
                          key={i}
                          sx={{
                            '&:hover': { backgroundColor: '#e3f2fd' },
                            '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' },
                          }}
                        >
                          <TableCell>{item.description || 'N/A'}</TableCell>
                          <TableCell>{formatCurrency(item.discountPerItem || 'N/A', quotation.currency || 'usd')}</TableCell>
                          <TableCell>{item.quantity?.quantityValue || 'N/A'}</TableCell>
                          <TableCell>{item.quantityUnit || 'N/A'}</TableCell>
                          <TableCell>{item.rate ? formatCurrency(item.rate, quotation.currency || 'usd') : 'N/A'}</TableCell>
                          <TableCell>{formatCurrency(item.totalCostPerItem || 'N/A', quotation.currency || 'usd')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </TableContainer>

                  <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: 1, boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', mt: 2 }}>
                  <Table
                    size={isTabletOrSmaller ? 'small' : 'medium'}
                    sx={{
                      width: isTabletOrSmaller ? '100%' : '50%',
                      marginLeft: isTabletOrSmaller ? 0 : 'auto',
                      marginRight: 0,
                      backgroundColor: '#f9f9f9',
                      borderRadius: 1,
                      border: '1px solid #e0e0e0',
                      minWidth: isTabletOrSmaller ? 420 : 'auto',
                    }}
                  >
                    <TableBody>
                      <TableRow sx={{ '&:hover': { backgroundColor: '#e3f2fd' } }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Total Before VAT</TableCell>
                        <TableCell>
                          {formatCurrency(quotation?.totalb4VAT || 'N/A', quotation.currency || 'usd')}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ '&:hover': { backgroundColor: '#e3f2fd' } }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Total VAT</TableCell>
                        <TableCell>{formatCurrency(quotation?.totalVAT || 'N/A', quotation.currency || 'usd')}</TableCell>
                      </TableRow>
                      <TableRow sx={{ '&:hover': { backgroundColor: '#e3f2fd' } }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Grand Total</TableCell>
                        <TableCell>{formatCurrency(quotation?.grandTotal || 'N/A', quotation.currency || 'usd')}</TableCell>
                      </TableRow>
                      <TableRow sx={{
                        backgroundColor: '#32CD32',
                        '&:hover': {
                          backgroundColor: '#F7C914'
                        },
                      }}>
                        <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Total Discount</TableCell>
                        <TableCell sx={{
                          color: '#ffffff',
                          fontWeight: 'bold'
                        }}>{quotation?.totalDiscount ? formatCurrency(quotation?.totalDiscount, quotation?.currency) : 'N/A'}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
          </>
        )}

        {procurementProcess?.purchaseOrderDocument?.url &&
          procurementProcess.purchaseOrderDocument.url.trim() !== '' && (
            <Divider sx={{ marginY: 2 }} />
          )}
        {procurementProcess?.purchaseOrderDocument?.url &&
          procurementProcess.purchaseOrderDocument.url.trim() !== '' && (
            <Typography variant="h5" gutterBottom color="secondary" sx={{ fontWeight: 'bold' }}>
              Purchase Order Document
            </Typography>
          )}


        {procurementProcess?.purchaseOrderDocument?.url &&
          procurementProcess.purchaseOrderDocument.url.trim() !== '' && (
            <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: 2, boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
            <Table size={isTabletOrSmaller ? 'small' : 'medium'} sx={{ backgroundColor: '#ffffff', borderRadius: 2, minWidth: isTabletOrSmaller ? 520 : 'auto' }}>
              <TableHead sx={{ backgroundColor: '#1976d2', color: 'white' }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Document Name</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {procurementProcess?.purchaseOrderDocument ? (

                  <TableRow sx={{ '&:hover': { backgroundColor: '#e8f0fe' } }}>
                    <TableCell>{procurementProcess?.purchaseOrderDocument.name}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleDialogOpen(procurementProcess?.purchaseOrderDocument.name)}
                        sx={{ color: '#1976d2' }}>
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                ) : (
                  <TableRow>
                    <TableCell colSpan={2}>No documents available</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </TableContainer>)}

        {procurementProcess?.cpoReview?.comment && (
          <Divider sx={{ marginY: 2 }} />)}

        {procurementProcess?.cpoReview?.comment && (
          <Typography variant="h5" gutterBottom color="secondary" sx={{ fontWeight: 'bold' }}>
            Supplier Recommendation Reviewers' Comments
          </Typography>)}

        {procurementProcess?.cpoReview?.comment && (
          <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: 2, boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
          <Table size={isTabletOrSmaller ? 'small' : 'medium'} sx={{ backgroundColor: '#ffffff', borderRadius: 2, minWidth: isTabletOrSmaller ? 720 : 'auto' }}>
            <TableHead sx={{ backgroundColor: '#1976d2', color: 'white' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date Reviewed</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reviewer</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Comment</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Decision</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>

              <ReviewerTableRow review={procurementProcess?.cpoReview} />
              <ReviewerTableRow review={procurementProcess?.proChairReview} />

              {!hasComments && (
                <TableRow>
                  <TableCell colSpan={4}>No comments from reviewers yet.</TableCell>
                </TableRow>
              )}

            </TableBody>
          </Table>
          </TableContainer>
        )}
        <br />

        {/* item transfers */}

        {itemTransfers && itemTransfers?.length > 0 &&
          (
            <Divider sx={{ marginY: 2 }} />
          )}
        {itemTransfers && itemTransfers?.length > 0 && (
          <Typography variant="h5" gutterBottom color="secondary" sx={{ fontWeight: 'bold' }}>
            Goods Received
          </Typography>
        )}


        {itemTransfers && itemTransfers?.length > 0 && (
          <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2, overflowX: 'auto' }}>
            <Table size={isTabletOrSmaller ? 'small' : 'medium'} sx={{ minWidth: isTabletOrSmaller ? 980 : 'auto' }}>
              <TableHead sx={{ backgroundColor: '#1976d2' }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Title</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Received By</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>PO Number</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Supplier</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Destination</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>GRV Document</TableCell>
                  {/* <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">GRV Document</TableCell> */}
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>GRV Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {itemTransfers && itemTransfers.length > 0 ? (
                  itemTransfers.map((transfer: any, index: number) => (
                    <TableRow key={transfer.id} sx={{ backgroundColor: index % 2 === 0 ? '#f0f4f7' : 'white' }}>
                      <TableCell>{transfer.title || 'N/A'}</TableCell>
                      <TableCell>{transfer.transferredBy.name || 'N/A'}</TableCell>
                      <TableCell>{transfer.purchaseOrderNumber || 'N/A'}</TableCell>
                      <TableCell>{getLocationName(transfer.sourceLocation)}</TableCell>
                      <TableCell>{getLocationName(transfer.destinationLocation)}</TableCell>
                      <TableCell>
                        {new Date(transfer.transferredDate).toLocaleDateString('en-GB', {
                          month: 'short', day: 'numeric', year: 'numeric'
                        })}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transfer.isService ? 'Service' : 'Goods'}
                          color={transfer.isService ? 'primary' : 'secondary'}
                        />
                      </TableCell>


                      <TableCell>
                        <IconButton
                          onClick={() => handleDialogOpen(transfer.grvDocument.name)}
                          sx={{ color: '#1976d2' }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>

                      <TableCell>
                        {formatCurrency(transfer.grandTotal, transfer.currency || 'usd')}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center">No goods received as yet</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>)}

        {purchaseOrders && purchaseOrders[0] && purchaseOrders[0]?.quotation?.grandTotal && (
          <Box sx={{ marginTop: 4, display: 'flex', justifyContent: 'center' }}>
            <TableContainer component={Paper} sx={{ overflowX: 'auto', width: '100%', maxWidth: 560 }}>
            <Table size={isTabletOrSmaller ? 'small' : 'medium'} sx={{ width: '100%', minWidth: 360, border: '1px solid #ccc', borderRadius: 2 }}>
              <TableBody>
                <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                  <TableCell>
                    <Typography variant="h6" fontWeight="bold">Total Goods Received</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{formatCurrency(grvTotal, purchaseOrders[0].quotation?.currency || 'usd')}</Typography>
                  </TableCell>
                </TableRow>

                <TableRow sx={{ backgroundColor: '#fce4ec' }}>
                  <TableCell>
                    <Typography variant="h6" fontWeight="bold">PO Grand Total</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{formatCurrency(purchaseOrders[0]?.quotation?.grandTotal, purchaseOrders[0].quotation?.currency || 'usd')}</Typography>
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{
                    backgroundColor:
                      purchaseOrders[0]?.quotation?.grandTotal - grvTotal > 0
                        ? '#e8f5e9'
                        : purchaseOrders[0]?.quotation?.grandTotal - grvTotal === 0
                          ? '#fff8e1'
                          : '#ffebee',
                  }}
                >
                  <TableCell>
                    <Typography variant="h6" fontWeight="bold">Remaining Balance</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="h6"
                      sx={{
                        color:
                          purchaseOrders[0]?.quotation?.grandTotal - grvTotal > 0
                            ? 'green'
                            : purchaseOrders[0]?.quotation?.grandTotal - grvTotal === 0
                              ? '#ff9800'
                              : 'red',
                        fontWeight: 'bold',
                      }}
                    >
                      {formatCurrency((purchaseOrders[0]?.quotation?.grandTotal - grvTotal), purchaseOrders[0].quotation?.currency || 'usd')}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            </TableContainer>
          </Box>
        )}
        <br></br>

        {/* payment details */}
        {showPaymentRequest && <PaymentRequest procurementProcess={procurementProcess} />}
        {procurementProcess?.cfoReview?.comment && (
          <Divider sx={{ marginY: 2 }} />)}
        {procurementProcess?.cfoReview?.comment && (
          <Typography variant="h5" gutterBottom color="secondary" sx={{ fontWeight: 'bold' }}>
            Payment Requisitions Reviewer's Comments
          </Typography>)}

        {procurementProcess?.cfoReview?.comment && (
          <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: 2, boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
          <Table size={isTabletOrSmaller ? 'small' : 'medium'} sx={{ backgroundColor: '#ffffff', borderRadius: 2, minWidth: isTabletOrSmaller ? 720 : 'auto' }}>
            <TableHead sx={{ backgroundColor: '#1976d2', color: 'white' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date Reviewed</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reviewer</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Comment</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Decision</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <ReviewerTableRow review={procurementProcess?.cfoReview} />
              <ReviewerTableRow review={procurementProcess?.cooReview} />
              <ReviewerTableRow review={procurementProcess?.ceoReview} />

              {!hasComments && (
                <TableRow>
                  <TableCell colSpan={4}>No comments from reviewers.</TableCell>
                </TableRow>
              )}

            </TableBody>
          </Table>
          </TableContainer>
        )}

        <br />

        <Dialog open={!!openDialog} onClose={handleDialogClose} fullWidth maxWidth="md">
          <Box p={2}>
            {openDialog && (
              <iframe
                src={openDialog}
                title="Document Viewer"
                width="100%"
                height={isTabletOrSmaller ? '360px' : '500px'}
                style={{ border: 'none' }}
              />
            )}
            <Box textAlign="right" mt={2}>
              <Button onClick={handleDialogClose} variant="contained" color="primary">
                Close
              </Button>
            </Box>
          </Box>
        </Dialog>


      </CardContent>
    </Card>
  );
};

export default RenderRequisitionDetails;
